import { Head } from "@/components/base/Head";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import css from "./index.module.scss";
import { FormSubmitButton } from "@/components/base/ui/FormSubmitButton2";
import { LpLayout } from "@/layouts/header-footer-none";
import * as Scroll from "react-scroll";
import { useRouter } from "next/router";
import { personalities } from "@/data/personalities";
import { CheckIcon, ChevronRightIcon } from "@heroicons/react/24/solid";

type Question = {
  step: number;
  items: QuestionItem[];
};

type QuestionItem = {
  title: string;
  isReverse: boolean;
};
let resultType = "";

const DiagnosisPage = () => {
  const META_TITLE = "診断中 | 戦略ラボ";
  const scroll = Scroll.animateScroll;
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<number>(0);

  const onSubmit = async (data: any) => {
    try {
      const result = Object.entries(data).reduce((prev, current) => {
        return prev + Number(current[1]);
      }, 0);
      switch (currentStep) {
        case 0:
          resultType = resultType + (result < 24 ? "E" : "I");
          break;
        case 1:
          resultType = resultType + (result < 24 ? "S" : "N");
          break;
        case 2:
          resultType = resultType + (result < 24 ? "F" : "T");
          break;
        case 3:
          resultType = resultType + (result < 24 ? "J" : "P");
          break;
      }
      if (currentStep < 3) {
        setCurrentStep(currentStep + 1);
      } else {
        const result = personalities.find(
          (personality) => personality.type === resultType
        );
        resultType = "";
        router.push(`/animal_diagnosis/results/${result?.id}`);
        return;
      }
      setLoading(true);
      scroll.scrollToTop();
      reset();
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const questions: Question[] = [
    {
      step: 0,
      items: [
        {
          isReverse: true,
          title: "多様な人とコミュニケーションするのが好きだ",
        },
        {
          isReverse: false,
          title: "どちらかと言うと話し役より聞き役になることが多い",
        },
        {
          isReverse: true,
          title: "情報共有は文章よりも口頭で伝える方が好きだ",
        },
        {
          isReverse: false,
          title: "新しいやり方より今のやり方を続けるほうが好きだ",
        },
        {
          isReverse: false,
          title: "お互いの意図を汲み取って動ける関係が好きだ",
        },
        {
          isReverse: false,
          title: "会議では自分一人の考えにふけることがよくある",
        },
        {
          isReverse: true,
          title: "知っている世界を掘り下げるより、知らない世界を冒険したい",
        },
        {
          isReverse: true,
          title:
            "1つの事業に集中するより複数のプロジェクトに横断的に関わりたい",
        },
      ],
    },
    {
      step: 1,
      items: [
        {
          isReverse: false,
          title: "細かいところより全体に目が行くほうである",
        },
        {
          isReverse: true,
          title: "アイデアマンより実行者でありたい",
        },
        {
          isReverse: false,
          title: "今までのやり方にとらわれず、独自の方法で動くことが多い",
        },
        {
          isReverse: false,
          title: "何かに集中するとあっという間に時間が過ぎてしまう",
        },
        {
          isReverse: false,
          title: "似たような出来事があると法則を知りたくなる",
        },
        {
          isReverse: true,
          title: "将来を考えるより今に集中することが大事だ",
        },
        {
          isReverse: true,
          title: "習慣やルーティーンを大事にしている",
        },
        {
          isReverse: true,
          title: "何かお願いされたら深く考えるよりすぐ行動に移す",
        },
      ],
    },
    {
      step: 2,
      items: [
        {
          isReverse: true,
          title: "プライベートな悩みを仕事仲間に理解して欲しい",
        },
        {
          isReverse: true,
          title: "仕事では結果よりプロセスが重要だ",
        },
        {
          isReverse: false,
          title: "理想の上司は干渉の少ない人だ",
        },
        {
          isReverse: false,
          title: "部下は甘やかすより厳しく接するべきだ",
        },
        {
          isReverse: true,
          title: "ルールや目的より仲間の和や人間関係を大事にしたい",
        },
        {
          isReverse: true,
          title: "交渉や議論の中でも相手を尊重することが大事だ",
        },
        {
          isReverse: false,
          title: "人の良い部分より悪い部分が目につきやすい",
        },
        {
          isReverse: false,
          title: "周りの意見より自分の意見を貫くほうである",
        },
      ],
    },
    {
      step: 3,
      items: [
        {
          isReverse: true,
          title: "正確なスケジュールが直前まで決まらないとイライラする",
        },
        {
          isReverse: false,
          title: "ルーチンワークは嫌いだ",
        },
        {
          isReverse: true,
          title: "物事は白黒はっきりさせたい",
        },
        {
          isReverse: false,
          title: "宿題も仕事も期日ギリギリまで残ってしまう",
        },
        {
          isReverse: false,
          title: "メモは全部取らず、大事な部分だけ取るようにしている",
        },
        {
          isReverse: true,
          title: "後輩が困っているとすぐに手を貸したくなる",
        },
        {
          isReverse: true,
          title:
            "新たな施策をやるときは、すぐに実行するよりリサーチや準備を大切にしたい",
        },
        {
          isReverse: false,
          title: "飲食店のメニューは多いほうが良い",
        },
      ],
    },
  ];

  return (
    <>
      <Head title={META_TITLE} canonicalPath={`/animal_diagnosis/diagnosis`} />
      <main className={"bg-[#F6F8FA] pt-10 pb-[120px]"}>
        <div className={"max-w-[420px] m-auto bg-white"}>
          <div className={"flex items-center justify-between bg-[#FBE9E9] p-8"}>
            {Array(4)
              .fill("")
              .map((value, index) => (
                <div key={index} className={"flex items-center"}>
                  <p
                    className={`${
                      index === currentStep
                        ? "bg-[#e9536c] text-white"
                        : "bg-white"
                    } py-2 w-[56px] text-center`}
                  >
                    {index + 1}
                  </p>
                  <span className={`${index + 1 === 4 ? "hidden" : "pl-4"}`}>
                    <ChevronRightIcon className={"w-3"} />
                  </span>
                </div>
              ))}
          </div>
          <div className={"p-6 md:p-10"}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {questions[currentStep]?.items.map((item, index) => (
                <div key={index} className={"mb-14"}>
                  <p
                    className={
                      "font-helvetica text-[#3eb034] text-2xl italic mb-1 tracking-[0.8px] font-bold"
                    }
                  >
                    Q
                    {index +
                      1 +
                      currentStep * 10 -
                      (currentStep ? 2 * currentStep : 0)}
                  </p>
                  <p className={"text-xl mb-4 font-bold"}>{item.title}</p>
                  <div className={"flex relative justify-between"}>
                    <div className={"relative"}>
                      <input
                        className={css.input}
                        type="radio"
                        id={`q-${index}-option-1`}
                        value={`${item.isReverse ? "5" : "1"}`}
                        {...register(String(index), { required: true })}
                      />
                      <label
                        className={css.label}
                        htmlFor={`q-${index}-option-1`}
                      >
                        <CheckIcon
                          className={
                            "fill-white absolute top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%]"
                          }
                        />
                      </label>
                      <p className={css.text}>全く違う</p>
                    </div>
                    <div>
                      <input
                        className={css.input}
                        type="radio"
                        id={`q-${index}-option-2`}
                        value={`${item.isReverse ? "4" : "2"}`}
                        {...register(String(index), { required: true })}
                      />
                      <label
                        className={css.label}
                        htmlFor={`q-${index}-option-2`}
                      >
                        <CheckIcon
                          className={
                            "fill-white absolute top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%]"
                          }
                        />
                      </label>
                    </div>
                    <div className={"relative"}>
                      <input
                        className={css.input}
                        type="radio"
                        id={`q-${index}-option-3`}
                        value="3"
                        {...register(String(index), { required: true })}
                      />
                      <label
                        className={css.label}
                        htmlFor={`q-${index}-option-3`}
                      >
                        <CheckIcon
                          className={
                            "fill-white absolute top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%]"
                          }
                        />
                      </label>
                      <p className={css.text}>
                        どちらとも
                        <br />
                        いえない
                      </p>
                    </div>
                    <div>
                      <input
                        className={css.input}
                        type="radio"
                        id={`q-${index}-option-4`}
                        value={`${item.isReverse ? "2" : "4"}`}
                        {...register(String(index), { required: true })}
                      />
                      <label
                        className={css.label}
                        htmlFor={`q-${index}-option-4`}
                      >
                        <CheckIcon
                          className={
                            "fill-white absolute top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%]"
                          }
                        />
                      </label>
                    </div>
                    <div className={"relative"}>
                      <input
                        className={css.input}
                        type="radio"
                        id={`q-${index}-option-5`}
                        value={`${item.isReverse ? "1" : "5"}`}
                        {...register(String(index), { required: true })}
                      />
                      <label
                        className={css.label}
                        htmlFor={`q-${index}-option-5`}
                      >
                        <CheckIcon
                          className={
                            "fill-white absolute top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%]"
                          }
                        />
                      </label>
                      <p className={css.text}>
                        とても
                        <br />
                        そうだ
                      </p>
                    </div>
                  </div>
                  {Object.keys(errors).includes(String(index)) && (
                    <span className={"block text-red-500 mt-9 text-sm"}>
                      {"回答は必須です。"}
                    </span>
                  )}
                </div>
              ))}
              <div className={"mt-20"}>
                <FormSubmitButton loading={loading}>
                  {currentStep === 3 ? "結果へ" : "次へ"}
                </FormSubmitButton>
              </div>
            </form>
          </div>
        </div>
      </main>
    </>
  );
};

DiagnosisPage.Layout = LpLayout;
export async function getStaticProps() {
  return {
    props: {},
  };
}
export default DiagnosisPage;
